/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { FormEvent, useEffect, useState } from 'react';
import { RadioButton } from 'primereact/radiobutton';
import { useTranslation } from 'react-i18next';
import { useAuthContext, useToastNotification } from '@/hooks';
import { Schedule } from '@/components/Schedule/types';
import { NotificationType } from '@/contexts/auth/AuthContext';

import { Button } from '@/components';
import { PagesPaths } from '@/enums';
import { useNavigate } from 'react-router-dom';
import { getSchedule } from '@/components/Schedule/getSchedule';
import { useMutation, useQuery } from '@tanstack/react-query';
import styles from './Notifications.module.scss';
import TripTable from './TripTable';
import ServiceTable from './ServiceTable';
import { viajeSuscripto } from './services/viajeSuscripto';
import { serviciosScheduler } from './services/serviciosScheduler';
import { guardarViajeSuscripcion } from './services/guardarViajeSuscripcion';
import { guardarServicioSuscripcion } from './services/guardarServicioSuscripcion';
import { serviciosSuscripto } from './services/serviciosSuscripto';
import { cambiarConfiguracion } from './services/cambiarConfiguracion';
import { desubscribirViajes } from './services/desubscribirViajes';
import { suscribirViajes } from './services/suscribirViajes';

export interface Servicio {}

const Notifications: React.FC = () => {
  const { showToast } = useToastNotification();
  const { userDataDecoded } = useAuthContext();

  const [selectedNotificationType, setSelectedNotificationType] = useState<
    NotificationType | null | string
  >(sessionStorage.getItem('valorNotificacion') ?? userDataDecoded.cambioseta);
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<Schedule[]>();
  const [viajeScheduleData, setViajeScheduleData] = useState<Schedule[]>();

  const [selectedServiceItems, setSelectedServiceItems] =
    useState<Schedule[]>();

  const { data: scheduleData, isLoading: isLoadingSchedule } = useQuery({
    queryKey: ['schedule'],
    queryFn: getSchedule,
  });

  const { data: serviciosScheduleData, isLoading: isLoadingserviciosSchedule } =
    useQuery({
      queryKey: ['serviciosSchedule'],
      queryFn: serviciosScheduler,
    });

  const { data: viajeSuscriptoData, isLoading: isLoadingViajeSuscrito } =
    useQuery({
      queryKey: ['viajeSuscripto'],
      queryFn: viajeSuscripto,
    });

  const { data: servicioSuscriptoData, isLoading: isLoadingServicioSuscrito } =
    useQuery({
      queryKey: ['servicioSuscripto'],
      queryFn: serviciosSuscripto,
    });

  const {
    isPending: viajeSuscripcionLoading,
    mutate: guardarViajeSuscripcionMutate,
  } = useMutation({
    mutationKey: ['guardarViajeSuscripcion'],
    mutationFn: guardarViajeSuscripcion,
    onSuccess: () => {
      showToast({
        severity: 'success',
        detail: 'Viaje/s suscripto guardado correctamente',
      });
    },
  });

  const {
    isPending: suscribirViajesLoading,
    mutate: guardarSuscribirViajesMutate,
  } = useMutation({
    mutationKey: ['viajeSuscripcion'],
    mutationFn: suscribirViajes,
  });

  const {
    isPending: viajedesuscripcionLoading,
    mutate: guardarViajedesbuscripcionMutate,
  } = useMutation({
    mutationKey: ['guardarViajeDesuscripcion'],
    mutationFn: () => desubscribirViajes(),
    onSuccess: () => {
      showToast({
        severity: 'success',
        detail: 'Viaje/s desubscripto correctamente',
      });
    },
  });

  const {
    isPending: servicioSuscripcionLoading,
    mutate: guardarServicioSuscripcionMutate,
  } = useMutation({
    mutationKey: ['guardarServicioSuscripcion'],
    mutationFn: () =>
      guardarServicioSuscripcion(
        selectedServiceItems?.map((val) => `${val.codigo}`)
      ),
    onSuccess: () => {
      showToast({
        severity: 'success',
        detail: 'Servicio/s suscripto guardado correctamente',
      });
    },
  });

  const formatNotificationType = (
    type: NotificationType | null | string
  ): string | undefined => {
    return type
      ?.replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/ ([A-Z])/g, ' $1'.toLowerCase());
  };

  const {
    isPending: cambiarConfiguracionnLoading,
    mutate: cambiarConfiguracionMutate,
  } = useMutation({
    mutationKey: ['cambiarConfiguracion'],
    mutationFn: () =>
      cambiarConfiguracion({
        nombre: 'montecon/web/notificaciones/cambioseta',
        valor: selectedNotificationType,
      }),
    onSuccess: () => {
      showToast({
        severity: 'success',
        detail: `Configuración cambiada correctamente a ${formatNotificationType(selectedNotificationType)}`,
      });
      sessionStorage.setItem(
        `valorNotificacion`,
        selectedNotificationType ?? ''
      );
    },
  });

  const handleSelectionChange = (e: { value: unknown }) => {
    setSelectedItems(e?.value as Schedule[]);
  };

  const handleServiceSelectionChange = (e: { value: unknown }) => {
    setSelectedServiceItems(e?.value as Schedule[]);
  };

  useEffect(() => {
    if (viajeSuscriptoData?.length && scheduleData?.length) {
      const matchedItems = scheduleData.filter((item: Schedule) =>
        viajeSuscriptoData.includes(item.codigo)
      );
      setSelectedItems(matchedItems);
    }
  }, [viajeSuscriptoData, scheduleData]);

  useEffect(() => {
    if (servicioSuscriptoData?.length && serviciosScheduleData?.length) {
      const matchedItems = serviciosScheduleData.filter((item: Schedule) =>
        servicioSuscriptoData.includes(item.codigo)
      );
      setSelectedServiceItems(matchedItems);
    }
  }, [servicioSuscriptoData, serviciosScheduleData]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    cambiarConfiguracionMutate();

    if (selectedNotificationType === 'Ninguna') {
      guardarViajedesbuscripcionMutate();
    }

    if (selectedNotificationType === 'Todas') {
      guardarSuscribirViajesMutate(true);
    }

    if (selectedNotificationType === 'PorViaje') {
      guardarSuscribirViajesMutate(false);
      guardarViajeSuscripcionMutate(selectedItems?.map((val) => val.codigo));
    }
    if (selectedNotificationType === 'PorServicio') {
      guardarSuscribirViajesMutate(false);
      guardarServicioSuscripcionMutate();
    }
  };

  useEffect(() => {
    if (scheduleData) {
      const newData = scheduleData?.map((item: any) => {
        const { ...rest } = item;
        return {
          ...rest,
          aperturaStacking: item?.aperturaStacking
            ? new Date(item?.aperturaStacking)
            : '',
          cierreStacking: item?.cierreStacking
            ? new Date(item?.cierreStacking)
            : '',
          llegada: item?.llegada ? new Date(item?.llegada) : '',
        };
      });
      setViajeScheduleData(newData);
    }
  }, [scheduleData]);

  return (
    <form onSubmit={handleSubmit} className={styles.notificationsContainer}>
      <div className={styles.radioButtonContainer}>
        <div className="mr-6">
          <RadioButton
            value="Ninguna"
            name="notificationType"
            inputId="Ninguna"
            onChange={(e) => setSelectedNotificationType(e.value)}
            checked={selectedNotificationType === 'Ninguna'}
          />
          <label htmlFor="Ninguna">{t('receiveNone')}</label>
        </div>

        <div className="mr-6">
          <RadioButton
            value="Todas"
            name="notificationType"
            inputId="Todas"
            onChange={(e) => setSelectedNotificationType(e.value)}
            checked={selectedNotificationType === 'Todas'}
          />
          <label htmlFor="Todas">{t('receiveAllNotifications')}</label>
        </div>

        <div className="mr-6">
          <RadioButton
            value="PorViaje"
            name="notificationType"
            inputId="PorViaje"
            onChange={(e) => setSelectedNotificationType(e.value)}
            checked={selectedNotificationType === 'PorViaje'}
          />
          <label htmlFor="PorViaje">{t('selectByTrip')}</label>
        </div>
        <div>
          <RadioButton
            value="PorServicio"
            name="notificationType"
            inputId="PorServicio"
            onChange={(e) => setSelectedNotificationType(e.value)}
            checked={selectedNotificationType === 'PorServicio'}
          />
          <label htmlFor="PorServicio">{t('selectByService')}</label>
        </div>
      </div>

      {selectedNotificationType === 'PorViaje' && (
        <TripTable
          data={viajeScheduleData}
          isLoading={isLoadingSchedule || isLoadingViajeSuscrito}
          selectedItems={selectedItems}
          handleSelectionChange={handleSelectionChange}
        />
      )}

      {selectedNotificationType === 'PorServicio' && (
        <ServiceTable
          data={serviciosScheduleData}
          isLoading={isLoadingserviciosSchedule}
          selectedItems={selectedServiceItems}
          handleSelectionChange={handleServiceSelectionChange}
        />
      )}

      <div className={styles.buttons}>
        <button
          type="button"
          onClick={() => {
            navigate(PagesPaths.PrivateHome);
          }}
          className={styles.secondaryActionsButtons}
        >
          {t('cancel')}
        </button>

        <Button
          className="w-auto px-3 mr-3"
          type="submit"
          green
          label={t('save')}
          loading={
            viajeSuscripcionLoading ||
            servicioSuscripcionLoading ||
            isLoadingServicioSuscrito ||
            cambiarConfiguracionnLoading ||
            viajedesuscripcionLoading ||
            suscribirViajesLoading
          }
        />
      </div>
    </form>
  );
};

export default Notifications;
